import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import EditText from '../../components/ui/EditText';
import Switch from '../../components/ui/Switch';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as URLS from '../../../urls';
import { getCountriesService, getProvincesService } from 'redux/services/general_service';

const Register = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl'; // للتحقق من RTL

  const dispatch = useDispatch();
  const { countries } = useSelector((state) => state.general);

  const [provinces, setProvinces] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    country_id: '',
    province_id: '',
    confirmPassword: '', // أضفت هذا للتوافق مع validation
    acceptTerms: false,  // أضفت هذا للـ Switch
  });

  useEffect(() => {
    dispatch(getCountriesService());
  }, [dispatch]);

  useEffect(() => {
    if (formData.country_id) {
      dispatch(getProvincesService(formData.country_id))
        .then((response) => {
          setProvinces(response.payload?.data?.data || []);
        })
        .catch((error) => {
          console.error('Error fetching provinces:', error);
          toast.warn(t('errorFetchingProvinces') || 'Error fetching provinces');
        });
    } else {
      setProvinces([]);
    }
  }, [formData.country_id, dispatch, t]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = `${t('firstNameRequired')}`;
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = `${t('lastNameRequired')}`;
    }

    if (!formData.email.trim()) {
      newErrors.email = `${t('emailRequired')}`;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = `${t('password') || 'Password'} is required`;
    } else if (formData.password.length < 8) {
      newErrors.password = `${t('password') || 'Password'} must be at least 8 characters`;
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = `${t('confirmPassword') || 'Confirm Password'} is required`;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('passwordsDoNotMatch') || "Passwords don't match";
    }

    if (formData.phone && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.country_id) {
      newErrors.country_id = `${t('country') || 'Country'} is required`;
    }

    if (!formData.province_id) {
      newErrors.province_id = `${t('province') || 'Province'} is required`;
    }

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return; // أعدت تفعيله
    }
    setIsLoading(true);
    try {
      const response = await axios.post(`${URLS.baseUrl}/${URLS?.registerURL}`, formData);
      if (response?.status === 200) {
        localStorage.setItem('Token', response?.data?.data?.token);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('permissions', JSON.stringify(response?.data?.data?.user?.permissions));
        localStorage.setItem('user', JSON.stringify({
          id: response.data.data?.user?.id,
          first_name: response.data.data?.user?.first_name,
          last_name: response.data.data?.user?.last_name,
          email: response.data.data?.user?.email,
        }));
        toast.success(t('registered_successfully') || 'Registered successfully');
        navigate('/');
      } else {
        response?.data?.errors?.forEach((error) => toast.warn(error));
      }
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      if (validationErrors) {
        Object.keys(validationErrors).forEach((key) => {
          validationErrors[key].forEach((message) => {
            toast.warn(t(message) || message);
          });
        });
      } else {
        toast.error(t('registrationFailed') || 'Registration failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchChange = (checked) => {
    setFormData((prev) => ({ ...prev, acceptTerms: checked }));
  };

  // دالة تبديل رؤية كلمة السر
  const togglePasswordVisibility = (setter) => () => {
    setter((prev) => !prev);
  };

  return (
    <>
      <Helmet>
        <title>{t('createAccount') || 'Create Account'} | Star Shopping Platform</title>
        <meta name="description" content={t('metaDescription') || 'Create your account...'} />
        <meta property="og:title" content={t('createAccount') || 'Create Account'} />
        <meta property="og:description" content={t('metaDescription') || 'Create your account...'} />
      </Helmet>
      <main className="w-full bg-white" dir={i18n.dir()}>
        <div className={`flex flex-col lg:flex-row min-h-screen ${isRTL ? 'flex-row-reverse' : ''}`}>
          {/* Left Image Section */}
          <section className="w-full lg:w-[58%] relative">
            <img
              src="/images/img_close_up_view_o.png"
              alt="Shopping cart with packages and credit card on green background"
              className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-full object-cover"
            />
          </section>

          {/* Right Form Section */}
          <section className={`w-full lg:w-[42%] flex flex-col px-4 sm:px-6 md:px-12 lg:ps-[78px] lg:pe-[56px] py-8 lg:py-0 ${isRTL ? 'lg:ps-[56px] lg:pe-[78px]' : ''}`}>
            <div className="w-full max-w-md mx-auto lg:max-w-none">
              {/* Logo */}
              <div className={`flex justify-center lg:justify-start mb-8 lg:mb-[38px] lg:${isRTL ? 'mr' : 'ml'}-[44px]`}>
                <img
                  src="/images/img_black_and_yellow_144x268.png"
                  alt="Star logo"
                  className="w-[200px] h-[108px] sm:w-[240px] sm:h-[129px] md:w-[268px] md:h-[144px] object-contain"
                />
              </div>

              {/* Form Container */}
              <div className="w-full">
                {/* Header */}
                <div className={`text-center lg:text-left mb-6 lg:mb-[34px] ${isRTL ? 'lg:text-right' : ''}`}>
                  <h1 className={`text-2xl sm:text-3xl lg:text-[28px] font-bold leading-tight lg:leading-[35px] text-black mb-2 lg:mb-2 lg:${isRTL ? 'mr' : 'ml'}-[56px] ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t("createAccount") || 'Create an Account'}
                  </h1>
                  <p className={`text-sm lg:text-[14px] font-medium leading-[17px] text-[#727272] lg:${isRTL ? 'mr' : 'ml'}-[44px] ${isRTL ? 'text-right' : ''}`}>
                    {t("joinCustomers") || 'Hello there, Let us start your journey with us.'}
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6 lg:space-y-[30px]" dir={i18n.dir()}>
                  <div className="space-y-4 lg:space-y-[22px]">
                    <div className="space-y-4 lg:space-y-[24px]">
                      {/* First Name Input */}
                      <div>
                        <EditText
                          placeholder={t("firstName") || 'First Name'}
                          text_font_size="14"
                          text_font_family="Gilroy-Medium"
                          text_color="#727272"
                          border_border="1 solid #d2d2d2"
                          border_border_radius="8px"
                          fill_background_color="#f6f6f6"
                          padding="14px"
                          value={formData?.first_name || ''}
                          onChange={handleInputChange}
                          type="text"
                          className="w-full"
                          layout_gap="0"
                          layout_width="100%"
                          position="relative"
                          variant="default"
                          size="medium"
                          name="first_name"
                          id="first_name"
                          text_text_align={isRTL ? "right" : "left"}
                        />
                        {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
                      </div>

                      {/* Last Name Input */}
                      <div>
                        <EditText
                          placeholder={t("lastName") || 'Last Name'}
                          text_font_size="14"
                          text_font_family="Gilroy-Medium"
                          text_color="#727272"
                          border_border="1 solid #d2d2d2"
                          border_border_radius="8px"
                          fill_background_color="#f6f6f6"
                          padding="14px"
                          value={formData?.last_name || ''}
                          onChange={handleInputChange}
                          type="text"
                          className="w-full"
                          layout_gap="0"
                          layout_width="100%"
                          position="relative"
                          variant="default"
                          size="medium"
                          name="last_name"
                          id="last_name"
                          text_text_align={isRTL ? "right" : "left"}
                        />
                        {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
                      </div>

                      {/* Email Input */}
                      <div>
                        <EditText
                          placeholder={t("email") || 'Email or phone number'}
                          text_font_size="14"
                          text_font_family="Gilroy-Medium"
                          text_color="#727272"
                          border_border="1 solid #d2d2d2"
                          border_border_radius="8px"
                          fill_background_color="#f6f6f6"
                          padding="14px"
                          value={formData?.email || ''}
                          onChange={handleInputChange}
                          type="text"
                          className="w-full"
                          layout_gap="0"
                          layout_width="100%"
                          position="relative"
                          variant="default"
                          size="medium"
                          name="email"
                          id="email"
                          text_text_align={isRTL ? "right" : "left"}
                        />
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                      </div>

                      {/* Phone Input */}
                      <div>
                        <EditText
                          placeholder={t("phone") || 'Phone Number'}
                          text_font_size="14"
                          text_font_family="Gilroy-Medium"
                          text_color="#727272"
                          border_border="1 solid #d2d2d2"
                          border_border_radius="8px"
                          fill_background_color="#f6f6f6"
                          padding="14px"
                          value={formData?.phone || ''}
                          onChange={handleInputChange}
                          type="text"
                          className="w-full"
                          layout_gap="0"
                          layout_width="100%"
                          position="relative"
                          variant="default"
                          size="medium"
                          name="phone"
                          id="phone"
                          text_text_align={isRTL ? "right" : "left"}
                        />
                        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                      </div>

                      {/* Password Input */}
                      <div className="relative">
                        <EditText
                          placeholder={t("password") || 'Enter password'}
                          text_font_size="14"
                          text_font_family="Gilroy-Medium"
                          text_color="#727272"
                          border_border="1 solid #d2d2d2"
                          border_border_radius="8px"
                          fill_background_color="#f6f6f6"
                          padding="14px 32px 14px 16px"
                          value={formData?.password || ''}
                          onChange={handleInputChange}
                          type={showPassword ? 'text' : 'password'}
                          className="w-full pr-12"
                          layout_gap="0"
                          layout_width="100%"
                          position="relative"
                          variant="default"
                          size="medium"
                          name="password"
                          id="password"
                          text_text_align={isRTL ? "right" : "left"}
                        />
                        <img
                          src="/images/img_loginicons.svg"
                          alt="Toggle password visibility"
                          className={`absolute ${isRTL ? 'start-4' : 'end-4'} top-1/2 transform -translate-y-1/2 w-4 h-4 cursor-pointer z-10`}
                          onClick={togglePasswordVisibility(setShowPassword)}
                        />
                        {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                      </div>

                      {/* Confirm Password Input */}
                      <div className="relative">
                        <EditText
                          placeholder={t("confirmPassword") || 'Confirm password'}
                          text_font_size="14"
                          text_font_family="Gilroy-Medium"
                          text_color="#727272"
                          border_border="1 solid #d2d2d2"
                          border_border_radius="8px"
                          fill_background_color="#f6f6f6"
                          padding="14px 32px 14px 16px"
                          value={formData?.confirmPassword || ''}
                          onChange={handleInputChange}
                          type={showConfirmPassword ? 'text' : 'password'}
                          className="w-full pr-12"
                          layout_gap="0"
                          layout_width="100%"
                          position="relative"
                          variant="default"
                          size="medium"
                          name="confirmPassword"
                          id="confirmPassword"
                          text_text_align={isRTL ? "right" : "left"}
                        />
                        <img
                          src="/images/img_loginicons.svg"
                          alt="Toggle confirm password visibility"
                          className={`absolute ${isRTL ? 'start-4' : 'end-4'} top-1/2 transform -translate-y-1/2 w-4 h-4 cursor-pointer z-10`}
                          onClick={togglePasswordVisibility(setShowConfirmPassword)}
                        />
                      </div>

                      {/* Country Selection */}
                      <div className="mb-4">
                        {' '}
                        {/* غيّرت mb-6 إلى mb-4 للتوافق مع space-y */}
                        <label
                          htmlFor="country_id"
                          className="block text-sm font-semibold text-[#727272] mb-2"
                        >
                          {t("country")}
                        </label>
                        <select
                          name="country_id"
                          value={formData.country_id || ''}
                          onChange={handleInputChange}
                          className={`block w-full p-[14px] border border-[#d2d2d2] bg-[#f6f6f6] rounded-[8px] text-[14px] font-medium text-[#727272] focus:outline-none focus:ring-2 focus:ring-[#16a3c7] transition duration-200 ease-in-out hover:border-[#16a3c7] ${
                            errors.country_id ? 'border-red-500' : ''
                          }`}
                          required
                        >
                          <option value="" disabled>
                            {t("selectCountry")}
                          </option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.id}>
                              {country.translations?.en?.name || country.name}
                            </option>
                          ))}
                        </select>
                        {errors.country_id && (
                          <p className="text-red-500 text-sm mt-1">{errors.country_id}</p>
                        )}
                      </div>

                      {/* Province Selection */}
                      <div className="mb-4">
                        <label
                          htmlFor="province_id"
                          className="block text-sm font-semibold text-[#727272] mb-2"
                        >
                            {t("province")}
                        </label>
                        <select
                          name="province_id"
                          value={formData.province_id || ''}
                          onChange={handleInputChange}
                          className={`block w-full p-[14px] border border-[#d2d2d2] bg-[#f6f6f6] rounded-[8px] text-[14px] font-medium text-[#727272] focus:outline-none focus:ring-2 focus:ring-[#16a3c7] transition duration-200 ease-in-out hover:border-[#16a3c7] ${
                            errors.province_id ? 'border-red-500' : ''
                          }`}
                          required
                        >
                          <option value="" disabled>
                           {t("selectProvince")}
                          </option>
                          {provinces?.map((province) => (
                            <option key={province.id} value={province.id}>
                              {province.name}
                            </option>
                          ))}
                        </select>
                        {errors.province_id && (
                          <p className="text-red-500 text-sm mt-1">{errors.province_id}</p>
                        )}
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="flex items-center justify-center lg:justify-start gap-2">
                      <Switch
                        checked={formData?.acceptTerms || false}
                        onChange={handleSwitchChange}
                        size="small"
                        className="flex-shrink-0"
                        name="acceptTerms"
                        id="acceptTerms"
                        aria-label="Accept terms and conditions"
                      />
                      <p className="text-xs leading-[18px] text-[#6c6c6c] font-normal">
                        <span>I accept the </span>
                        <span className="text-[#16a3c7] underline leading-[18px] cursor-pointer">
                          {t("termsOfService")}
                        </span>
                        <span> & </span>
                        <span className="text-[#16a3c7] underline leading-[18px] cursor-pointer">
                          {t("privacyPolicy")}
                        </span>
                      </p>
                    </div>

                    {/* Sign Up Button */}
                    <Button
                      text={isLoading ? t('loading') : t('createAccount')}
                      text_font_size="16"
                      text_font_family="Gilroy-Bold"
                      text_line_height="20px"
                      text_text_align="center"
                      text_color="#ffffff"
                      fill_background_color="#ca0d2d"
                      border_border_radius="8px"
                      padding="12px 34px"
                      onClick={handleSubmit}
                      disabled={!formData?.acceptTerms}
                      className="w-full"
                      layout_width="100%"
                      position="relative"
                      variant="primary"
                      size="medium"
                    />
                  </div>

                  {/* Divider Line */}
                  <div className="w-full h-[1px] bg-[#d2d2d2]"></div>

                  {/* Login Link */}
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-xs font-medium leading-[15px] text-[#727272]">
                      {t("alreadyHaveAccount")}
                    </span>
                    <Link
                      to="/login"
                      className="text-xs font-bold leading-[15px] text-[#ca0d2d] hover:underline"
                    >
                     {t("signInHere")}
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default Register;
